import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react";
import dayjs from "dayjs";
import { HttpService } from "../../service/HttpService";
import { useAuth } from "../../context/AuthProvider";
import FullScreenLoader from "../../components/Loader/FullScreenLoader";
import { toast } from "react-toastify";
import { Popover, MenuItem } from "@mui/material";
import {
  FiEdit3,
  FiX,
  FiSettings,
  FiSave,
  FiBold,
  FiItalic,
  FiUnderline,
  FiAlignLeft,
  FiAlignCenter,
  FiAlignRight,
  FiAlignJustify,
  FiList,
  FiType,
  FiDroplet,
  FiRotateCcw,
  FiChevronDown,
  FiMoreVertical,
} from "react-icons/fi";
import "./GrowRoom.css";
import frostedFarmLogo from "../../assets/img/frostedFarmBg.svg";
import route420 from "../../assets/img/route420bg.svg";

const COLUMNS = [
  { key: "action", label: "Action" },
  { key: "room", label: "Room" },
  { key: "day_temp_rh", label: "Day Temp/RH" },
  { key: "night_temp_rh", label: "Night Temp/RH" },
  { key: "cultivar", label: "Cultivar" },
  { key: "stage", label: "Stage" },
  { key: "target_PPM", label: "Target PPM" },
  { key: "lights", label: "Under Canopy/Side Lights" },
  { key: "light_pct", label: "Actual Light %" },
  { key: "planted", label: "Planted" },
  { key: "pre_flip", label: "Pre-Flip" },
  { key: "flip", label: "Flip" },
  { key: "day_21", label: "21-Day" },
  { key: "day_40", label: "40-Day" },
  { key: "harvest", label: "Harvest" },
  { key: "notes", label: "Notes" },
  { key: "spray", label: "Spray Schedule" },
];

const PRESET_COLORS = [
  "#000000",
  "#1a73e8", // Google/Material Blue
  "#2979FF", // Brand Blue
  "#00A272", // Brand Green
  "#ff0000",
  "#ff9900",
  "#ffff00",
  "#00ff00",
  "#00ffff",
  "#0000ff",
  "#9900ff",
  "#ff00ff",
  "#444444",
  "#666666",
  "#999999",
  "#cccccc",
  "#eeeeee",
  "#f3f3f3",
  "#ffffff",
  "#f4cccc",
  "#fce5cd",
  "#fff2cc",
  "#d9ead3",
  "#d0e0e3",
  "#cfe2f3",
  "#d9d2e9",
  "#ead1dc",
  "#ea9999",
  "#f9cb9c",
  "#ffe599",
  "#b6d7a8",
  "#a2c4c9",
  "#9fc5e8",
  "#b4a7d6",
  "#d5a6bd",
  "#e06666",
  "#f6b26b",
  "#ffd966",
  "#93c47d",
  "#76a5af",
  "#6fa8dc",
  "#8e7cc3",
  "#c27ba0",
  "#cc0000",
  "#e69138",
  "#f1c232",
  "#6aa84f",
  "#45818e",
  "#3d85c6",
  "#674ea7",
  "#a64d79",
  "#990000",
  "#b45f06",
  "#bf9000",
  "#38761d",
  "#134f5c",
  "#0b5394",
  "#351c75",
  "#741b47",
  "#660000",
  "#783f04",
  "#7f6000",
  "#274e13",
  "#0c343d",
  "#073763",
  "#20124d",
  "#4c1130",
];

const INITIAL_ROWS = [
  ...Array.from({ length: 12 }, (_, i) => ({
    row_key: `room_${i + 1}`,
    row_label: `${i + 1}`,
    sort_order: i + 1,
    column_data: { room: `${i + 1}` },
    column_styles: {
      room: {
        fontWeight: "900",
        color: "#2979FF",
        fontStyle: "italic",
        fontSize: "24px",
      },
    },
  })),
  {
    row_key: "clone",
    row_label: "Clone",
    sort_order: 100,
    column_data: { room: "Clone" },
  },
  {
    row_key: "momma",
    row_label: "Momma",
    sort_order: 101,
    column_data: { room: "Momma" },
  },
  {
    row_key: "veg_1",
    row_label: "Veg 1",
    sort_order: 102,
    column_data: { room: "Veg 1" },
  },
  {
    row_key: "temp_day",
    row_label: "Temp Day",
    sort_order: 200,
    column_data: { room: "77/66% [Day]" },
  },
  {
    row_key: "temp_night",
    row_label: "Temp Night",
    sort_order: 201,
    column_data: { room: "69/57% [Night]" },
  },
  {
    row_key: "fans",
    row_label: "Fans",
    sort_order: 202,
    column_data: { room: "Fans:" },
  },
  {
    row_key: "fans_labels",
    row_label: "Fans Labels",
    sort_order: 203,
    column_data: { room: "Fans:" },
  },
];

const EditableCell = React.forwardRef(
  ({ value, style, onChange, onFocus, onBlur }, ref) => {
    const cellRef = useRef(null);
    const lastValue = useRef(value);

    // Merge forwarded ref with internal ref
    useLayoutEffect(() => {
      if (typeof ref === "function") {
        ref(cellRef.current);
      } else if (ref) {
        ref.current = cellRef.current;
      }
    }, [ref]);

    useLayoutEffect(() => {
      if (cellRef.current && document.activeElement !== cellRef.current) {
        cellRef.current.innerHTML = value || "";
        lastValue.current = value;
      }
    }, [value]);

    const handleInput = (e) => {
      const newValue = e.currentTarget.innerHTML;
      lastValue.current = newValue;
      onChange(newValue);
    };



    return (
      <div
        ref={cellRef}
        className="grow-room-editable"
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onFocus={onFocus}
        onBlur={onBlur}
        style={style}
      />
    );
  },
);

const StylePopover = ({ styles, onChange, onClose, position }) => {
  return (
    <div
      className="style-popover"
      style={{ top: position.y, left: position.x }}
    >
      <div className="style-option-group">
        <span className="style-option-label">Text Color</span>
        <div className="color-grid">
          {PRESET_COLORS.slice(0, 18).map((color) => (
            <div
              key={color}
              className="color-swatch"
              style={{ backgroundColor: color }}
              onMouseDown={(e) => e.preventDefault()} // Prevent focus loss
              onClick={() => onChange({ ...styles, color })}
            />
          ))}
        </div>
      </div>

      <div className="style-option-group">
        <span className="style-option-label">Background Color</span>
        <div className="color-grid">
          {[
            "#fce5cd",
            "#fff2cc",
            "#d9ead3",
            "#d0e0e3",
            "#cfe2f3",
            "#d9d2e9",
            "#ead1dc",
            "#ffffff",
            "#f9cb9c",
            "#ffe599",
            "#b6d7a8",
            "#a2c4c9",
          ].map((color) => (
            <div
              key={color}
              className="color-swatch"
              style={{ backgroundColor: color }}
              onMouseDown={(e) => e.preventDefault()} // Prevent focus loss
              onClick={() => onChange({ ...styles, backgroundColor: color })}
            />
          ))}
        </div>
        <div className="custom-color-row" style={{ marginTop: "8px" }}>
          <input
            type="color"
            className="custom-color-input"
            onChange={(e) =>
              onChange({ ...styles, backgroundColor: e.target.value })
            }
            value={
              styles.backgroundColor && styles.backgroundColor.startsWith("#")
                ? styles.backgroundColor
                : "#ffffff"
            }
            title="Custom RGB Highlight"
          />
          <div className="hex-input-field">
            <span>#</span>
            <input
              type="text"
              placeholder="HEX"
              maxLength={6}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onChange({
                    ...styles,
                    backgroundColor: `#${e.target.value}`,
                  });
                }
              }}
              onBlur={(e) => {
                const val = e.target.value;
                if (val.length === 6 || val.length === 3) {
                  onChange({ ...styles, backgroundColor: `#${val}` });
                }
              }}
            />
          </div>
        </div>
        <button
          className="font-btn"
          style={{ marginTop: "4px", fontSize: "10px" }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() =>
            onChange({ ...styles, backgroundColor: "transparent" })
          }
        >
          Clear Background
        </button>
      </div>

      <div className="style-option-group">
        <span className="style-option-label">Font Style</span>
        <div className="font-options">
          <button
            className={`font-btn ${styles.fontWeight === "bold" ? "active" : ""}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() =>
              onChange({
                ...styles,
                fontWeight: styles.fontWeight === "bold" ? "normal" : "bold",
              })
            }
          >
            Bold
          </button>
          <button
            className={`font-btn ${styles.fontStyle === "italic" ? "active" : ""}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() =>
              onChange({
                ...styles,
                fontStyle: styles.fontStyle === "italic" ? "normal" : "italic",
              })
            }
          >
            Italic
          </button>
        </div>
      </div>
      <button
        className="grow-room-btn grow-room-btn-primary"
        onClick={onClose}
        style={{ padding: "6px", fontSize: "12px" }}
      >
        Close
      </button>
    </div>
  );
};

const RichTextToolbar = ({ onCommand, position }) => {
  const [activeDropdown, setActiveDropdown] = useState(null); // 'font', 'color', 'highlight'
  const isNearRight = position.x + 550 > window.innerWidth;

  const toggleDropdown = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveDropdown(activeDropdown === type ? null : type);
  };

  useEffect(() => {
    const handleGlobalClick = (e) => {
      if (!e.target.closest(".rich-text-toolbar-full")) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("click", handleGlobalClick, true);
    return () => document.removeEventListener("click", handleGlobalClick, true);
  }, []);

  return (
    <div
      className={`rich-text-toolbar-full ${isNearRight ? "near-right" : ""}`}
      style={{ top: position.y, left: position.x }}
      onMouseDown={(e) => e.stopPropagation()} // Prevent clicking toolbar from saving row
    >
      <div className="toolbar-group">
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            onCommand("bold");
          }}
          title="Bold"
        >
          <FiBold />
        </button>
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            onCommand("italic");
          }}
          title="Italic"
        >
          <FiItalic />
        </button>
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            onCommand("underline");
          }}
          title="Underline"
        >
          <FiUnderline />
        </button>
      </div>

      <div className="toolbar-divider" />

      <div className="toolbar-group">
        <div className="toolbar-select-wrapper">
          <button
            title="Font Size"
            style={{ width: "50px" }}
            onMouseDown={(e) => toggleDropdown(e, "font")}
          >
            {{
              1: "10",
              2: "13",
              3: "16",
              4: "18",
              5: "24",
              6: "32",
              7: "48",
            }[document.queryCommandValue("fontSize")] || "16"}
            <FiChevronDown size={12} />
          </button>
          <div
            className={`toolbar-dropdown-list ${activeDropdown === "font" ? "show" : ""}`}
          >
            <div
              onMouseDown={(e) => {
                e.preventDefault();
                onCommand("fontSize", "1");
              }}
            >
              10
            </div>
            <div
              onMouseDown={(e) => {
                e.preventDefault();
                onCommand("fontSize", "2");
              }}
            >
              13
            </div>
            <div
              onMouseDown={(e) => {
                e.preventDefault();
                onCommand("fontSize", "3");
              }}
            >
              16
            </div>
            <div
              onMouseDown={(e) => {
                e.preventDefault();
                onCommand("fontSize", "4");
              }}
            >
              18
            </div>
            <div
              onMouseDown={(e) => {
                e.preventDefault();
                onCommand("fontSize", "5");
              }}
            >
              24
            </div>
            <div
              onMouseDown={(e) => {
                e.preventDefault();
                onCommand("fontSize", "6");
              }}
            >
              32
            </div>
            <div
              onMouseDown={(e) => {
                e.preventDefault();
                onCommand("fontSize", "7");
              }}
            >
              48
            </div>
          </div>
        </div>
      </div>

      <div className="toolbar-divider" />

      <div className="toolbar-group">
        <div
          className="color-picker-wrapper"
          onMouseDown={(e) => e.preventDefault()}
        >
          <button
            title="Text Color"
            onMouseDown={(e) => toggleDropdown(e, "color")}
            style={{
              borderBottom: `3px solid ${document.queryCommandValue("foreColor") || "#000"}`,
            }}
          >
            <FiType />
          </button>
          <div
            className={`color-dropdown ${activeDropdown === "color" ? "show" : ""}`}
          >
            {PRESET_COLORS.map((c) => (
              <div
                key={c}
                className="color-opt"
                style={{
                  background: c,
                  outline:
                    document.queryCommandValue("foreColor") === c ||
                    document.queryCommandValue("foreColor") ===
                      `rgb(${parseInt(c.slice(1, 3), 16)}, ${parseInt(c.slice(3, 5), 16)}, ${parseInt(c.slice(5, 7), 16)})`
                      ? "2px solid #1a73e8"
                      : "none",
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onCommand("foreColor", c);
                }}
              />
            ))}
            <div className="custom-color-row">
              <input
                type="color"
                className="custom-color-input"
                onChange={(e) => onCommand("foreColor", e.target.value)}
                value={document.queryCommandValue("foreColor") || "#000000"}
                title="Custom RGB Color"
              />
              <div className="hex-input-field">
                <span>#</span>
                <input
                  type="text"
                  placeholder="HEX"
                  maxLength={6}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onCommand("foreColor", `#${e.target.value}`);
                    }
                  }}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val.length === 6 || val.length === 3) {
                      onCommand("foreColor", `#${val}`);
                    }
                  }}
                />
              </div>
            </div>
            <button
              className="dropdown-close-btn"
              onMouseDown={(e) => {
                e.preventDefault();
                setActiveDropdown(null);
              }}
            >
              Close
            </button>
          </div>
        </div>
        <div
          className="color-picker-wrapper"
          onMouseDown={(e) => e.preventDefault()}
        >
          <button
            title="Highlight"
            onMouseDown={(e) => toggleDropdown(e, "highlight")}
            style={{
              color: document.queryCommandValue("hiliteColor") || "#3c4043",
            }}
          >
            <FiDroplet />
          </button>
          <div
            className={`color-dropdown ${activeDropdown === "highlight" ? "show" : ""}`}
          >
            {[
              "#e8f0fe",
              "#e6f4ea",
              "#fef7e0",
              "#fce8e6",
              "#f3e8fd",
              "#ffffff",
            ].map((c) => (
              <div
                key={c}
                className="color-opt"
                style={{ background: c }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onCommand("hiliteColor", c);
                }}
              />
            ))}
            <div className="custom-color-row">
              <input
                type="color"
                className="custom-color-input"
                onChange={(e) => onCommand("hiliteColor", e.target.value)}
                value={document.queryCommandValue("hiliteColor") || "#ffffff"}
                title="Custom RGB Highlight"
              />
              <div className="hex-input-field">
                <span>#</span>
                <input
                  type="text"
                  placeholder="HEX"
                  maxLength={6}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onCommand("hiliteColor", `#${e.target.value}`);
                    }
                  }}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val.length === 6 || val.length === 3) {
                      onCommand("hiliteColor", `#${val}`);
                    }
                  }}
                />
              </div>
            </div>
            <button
              className="clear-bg-btn"
              onMouseDown={(e) => {
                e.preventDefault();
                onCommand("hiliteColor", "transparent");
              }}
            >
              Clear Background
            </button>
            <button
              className="dropdown-close-btn"
              onMouseDown={(e) => {
                e.preventDefault();
                setActiveDropdown(null);
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      <div className="toolbar-divider" />

      <div className="toolbar-group">
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            onCommand("justifyLeft");
          }}
          title="Left"
        >
          <FiAlignLeft />
        </button>
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            onCommand("justifyCenter");
          }}
          title="Center"
        >
          <FiAlignCenter />
        </button>
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            onCommand("justifyRight");
          }}
          title="Right"
        >
          <FiAlignRight />
        </button>
      </div>

      <div className="toolbar-divider" />

      <div className="toolbar-group">
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            onCommand("subscript");
          }}
          title="Subscript"
          style={{ fontSize: "10px" }}
        >
          x₂
        </button>
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            onCommand("superscript");
          }}
          title="Superscript"
          style={{ fontSize: "10px" }}
        >
          x²
        </button>
      </div>

      <div className="toolbar-divider" />

      <div className="toolbar-group">
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            onCommand("insertUnorderedList");
          }}
          title="List"
        >
          <FiList />
        </button>
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            onCommand("removeFormat");
          }}
          title="Clear"
        >
          <FiRotateCcw />
        </button>
      </div>
    </div>
  );
};

const GrowRoom = () => {
  const { setShowLoginPopup } = useAuth();
  const httpService = new HttpService(setShowLoginPopup);
  const [rows, setRows] = useState([]);
  const [phaseNotes, setPhaseNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingRowKey, setEditingRowKey] = useState(null);
  const [editData, setEditData] = useState(null);
  const editDataRef = useRef(null);
  const editingRowKeyRef = useRef(null);

  // Keep refs in sync with state for event listeners
  useEffect(() => {
    editDataRef.current = editData;
  }, [editData]);

  useEffect(() => {
    editingRowKeyRef.current = editingRowKey;
  }, [editingRowKey]);


  const [activeStyleCell, setActiveStyleCell] = useState(null);
  const [activeToolbar, setActiveToolbar] = useState(null);
  const blurTimerRef = useRef(null);
  const cellRefs = useRef({});
  const mainTableWrapperRef = useRef(null);
  const phaseNotesWrapperRef = useRef(null);
  const activeElementRef = useRef(null);
  const activeToolbarRef = useRef(null);
  const activeStyleCellRef = useRef(null);

  useEffect(() => {
    activeToolbarRef.current = activeToolbar;
  }, [activeToolbar]);

  useEffect(() => {
    activeStyleCellRef.current = activeStyleCell;
  }, [activeStyleCell]);

  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [actionRowData, setActionRowData] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch Grow Room Data
      const res = await httpService.get(`grow-room?t=${new Date().getTime()}`);
      const rowData = res?.data?.data || res?.data;
      

      if (Array.isArray(rowData)) {
        const mergedRows = INITIAL_ROWS.map((templateRow) => {
          const dbRow = rowData.find(
            (db) => db.row_key === templateRow.row_key,
          );
          return dbRow ? dbRow : templateRow;
        });
        setRows(mergedRows);
      } else {
        setRows(INITIAL_ROWS);
      }

      // Fetch Phase Notes
      const phaseRes = await httpService.get("grow-room/phase-notes");
      // phaseRes is the body: { status: true, data: [...] }
      if (phaseRes?.status && Array.isArray(phaseRes.data)) {
        setPhaseNotes(phaseRes.data);
      } else if (Array.isArray(phaseRes)) {
        // Fallback in case HttpService returns just the data array
        setPhaseNotes(phaseRes);
      }
    } catch (error) {
      console.error("Fetch Error:", error);
      setRows(INITIAL_ROWS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  // Effect to handle text selection when styling popover opens
  useEffect(() => {
    if (activeStyleCell) {
      const cellId = `${editingRowKey}-${activeStyleCell.colKey}`;
      const element = cellRefs.current[cellId];
      if (element) {
        // Use a small timeout to ensure the element is ready
        setTimeout(() => {
          const range = document.createRange();
          const selection = window.getSelection();
          range.selectNodeContents(element);
          selection.removeAllRanges();
          selection.addRange(range);
        }, 50);
      }
    }
  }, [activeStyleCell, editingRowKey]);

  const handleRowEditStart = (row, isPhaseNote = false) => {
    if (isPhaseNote) {
      // For phase notes, we structure editData to match the phase note model
      setEditData({ ...row, isPhaseNote: true });
      setEditingRowKey(row.stage_label);
    } else {
      setEditData(JSON.parse(JSON.stringify(row)));
      setEditingRowKey(row.row_key);
    }
  };

  const handleRowEditCancel = () => {
    setEditingRowKey(null);
    setEditData(null);
    setActiveStyleCell(null);
    setActiveToolbar(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const currentId = editingRowKeyRef.current;
      const currentValues = editDataRef.current;

      if (!currentId || !currentValues) return;

      const isPopover = !!event.target.closest(
        ".style-popover, .rich-text-toolbar-full, .rich-text-toolbar, .toolbar-dropdown-list, .color-dropdown, .MuiPopover-root, .MuiPickersPopper-root",
      );
      if (isPopover) return;

      // If clicking directly on the scrollable container (scrollbar), don't save
      if (
        event.target.classList.contains("grow-room-table-wrapper") ||
        event.target.classList.contains("grow-room-phase-notes")
      ) {
        return;
      }

      const isInside = event.target.closest(`[data-row-key="${currentId}"]`);
      if (!isInside) {
        handleRowEditSave(currentId, currentValues);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleRowEditSave = async (rowKey, valuesToSave) => {
    const data = valuesToSave || editData;
    if (!data) return;

    setLoading(true);
    try {
      let res;
      if (data.isPhaseNote) {
        if (!data.id) {
          console.error("Phase note ID is missing in data:", data);
          toast.error("Cannot save: Phase note ID is missing");
          return;
        }
        res = await httpService.patch(`grow-room/phase-notes/${data.id}`, {
          note_value: data.note_value,
          room: data.room || "",
          column_styles: data.column_styles || {},
        });
      } else {
        res = await httpService.patch(`grow-room/row/${rowKey}`, data);
      }

      if (res?.data?.status || res?.status) {
        toast.success("Row updated successfully");
        setEditingRowKey(null);
        setEditData(null);
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to save data");
    } finally {
      setLoading(false);
    }
  };

  const handleCellChange = (colKey, value) => {
    if (editData.isPhaseNote) {
      setEditData((prev) => ({
        ...prev,
        [colKey === "notes" ? "note_value" : colKey]: value,
        column_styles: prev.column_styles || {},
      }));
    } else {
      setEditData((prev) => ({
        ...prev,
        column_data: { ...prev.column_data, [colKey]: value },
      }));
    }
  };

  const handleStyleChange = (colKey, styleUpdates) => {
    setEditData((prev) => ({
      ...prev,
      column_styles: { ...prev.column_styles, [colKey]: styleUpdates },
    }));
  };

  const handleCommand = (command, value = null) => {
    document.execCommand(command, false, value);
  };

  const handleFocus = (e) => {
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }

    const isPhaseNote = !!e.currentTarget.closest(".phase-notes-table");
    activeElementRef.current = e.currentTarget;

    const container = document.querySelector(".grow-room-container");
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const cellRect = e.currentTarget.closest("td").getBoundingClientRect();
    const toolbarWidth = 520;
    const toolbarHeight = 40;

    let x = cellRect.left - containerRect.left - 40;
    let y = cellRect.bottom - containerRect.top + 2;

    // Safety check for right edge
    if (x + toolbarWidth > containerRect.width) {
      x = containerRect.width - toolbarWidth - 10;
    }
    x = Math.max(10, x);

    // Safety check for bottom edge
    if (y + toolbarHeight > containerRect.height) {
      y = cellRect.top - containerRect.top - 50;
    }

    setActiveToolbar({ x, y: Math.max(0, y) });
  };

  const handleBlur = (e) => {
    if (e.relatedTarget?.closest(".rich-text-toolbar-full")) {
      return;
    }
    blurTimerRef.current = setTimeout(() => {
      setActiveToolbar(null);
      blurTimerRef.current = null;
    }, 200);
  };

  const handleActionClick = (event, row, isPhaseNote) => {
    setActionAnchorEl(event.currentTarget);
    setActionRowData({ row, isPhaseNote });
  };

  const handleActionClose = () => {
    setActionAnchorEl(null);
    setActionRowData(null);
  };

  const handleEditFromAction = () => {
    if (actionRowData) {
      handleRowEditStart(actionRowData.row, actionRowData.isPhaseNote);
    }
    handleActionClose();
  };

  const openStylePopover = (e, colKey) => {
    activeElementRef.current = e.currentTarget;
    const container = document.querySelector(".grow-room-container");
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const cellRect = e.currentTarget.closest("td").getBoundingClientRect();
    const popoverWidth = 240;

    let x = cellRect.left - containerRect.left - popoverWidth;
    if (x < 10) x = cellRect.right - containerRect.left + 2;

    if (x + popoverWidth > containerRect.width) {
      x = containerRect.width - popoverWidth - 10;
    }

    setActiveStyleCell({
      colKey,
      x,
      y: cellRect.top - containerRect.top,
    });
  };

  // Update floating UI position on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!activeElementRef.current) return;

      const td = activeElementRef.current.closest("td");
      if (!td) return;

      const container = document.querySelector(".grow-room-container");
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const cellRect = td.getBoundingClientRect();

      if (activeToolbarRef.current) {
        const toolbarWidth = 520;
        const toolbarHeight = 40;
        let x = cellRect.left - containerRect.left - 40;
        let y = cellRect.bottom - containerRect.top + 2;
        if (x + toolbarWidth > containerRect.width)
          x = containerRect.width - toolbarWidth - 10;
        x = Math.max(10, x);
        if (y + toolbarHeight > containerRect.height)
          y = cellRect.top - containerRect.top - 50;
        setActiveToolbar({ x, y: Math.max(0, y) });
      }

      if (activeStyleCellRef.current) {
        const popoverWidth = 240;
        let x = cellRect.left - containerRect.left - popoverWidth;
        if (x < 10) x = cellRect.right - containerRect.left + 2;
        if (x + popoverWidth > containerRect.width)
          x = containerRect.width - popoverWidth - 10;
        setActiveStyleCell((prev) => ({
          ...prev,
          x,
          y: cellRect.top - containerRect.top,
        }));
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, []);

  const renderCell = (row, col, isPhaseNote = false) => {
    const isRowEditing =
      editingRowKey === (isPhaseNote ? row.stage_label : row.row_key);
    const rowData = isRowEditing ? editData : row;

    // For phase notes, we map data differently
    const val = isPhaseNote
      ? col.key === "notes"
        ? rowData.note_value
        : col.key === "stage"
          ? (rowData.stage_label || "")
              .replace("phase_", "")
              .toUpperCase()
              .replace("_", " ")
          : rowData[col.key]
      : rowData?.column_data?.[col.key] || "";

    const style = rowData?.column_styles?.[col.key] || {};
    const merge = isPhaseNote ? {} : rowData?.column_merges?.[col.key] || {};


    if (merge.isHidden) return null;

    if (col.key === "action") {
      return (
        <td
          key={col.key}
          className="action-column-cell"
          data-row-key={isPhaseNote ? row.stage_label : row.row_key}
        >
          <div className="action-btns-wrapper">
            {!isRowEditing ? (
              <button
                className="action-btn edit-btn"
                onClick={(e) => handleActionClick(e, row, isPhaseNote)}
                title="Action"
              >
                <FiMoreVertical size={16} />
              </button>
            ) : (
              <button
                className="action-btn cancel-btn"
                onClick={handleRowEditCancel}
                title="Cancel"
              >
                <FiX size={16} />
              </button>
            )}
          </div>
        </td>
      );
    }

    if (isRowEditing) {
      // Stage in phase notes is read-only even in edit mode
      if (isPhaseNote && col.key === "stage") {
        return (
          <td key={col.key} style={style}>
            {val}
          </td>
        );
      }

      return (
        <td
          key={col.key}
          rowSpan={merge.rowSpan}
          colSpan={merge.colSpan}
          data-row-key={isPhaseNote ? row.stage_label : row.row_key}
          className={`${
            col.key === "room" &&
            (row.row_key?.startsWith("room_") ||
              [
                "clone",
                "momma",
                "veg_1",
                "temp_day",
                "temp_night",
                "fans",
                "fans_labels",
              ].includes(row.row_key))
              ? "room-id-cell"
              : ""
          } col-${col.key}`}
        >
          <EditableCell
            value={val}
            style={style}
            onChange={(newVal) => handleCellChange(col.key, newVal)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            ref={(el) => {
              const cellId = `${isPhaseNote ? row.stage_label : row.row_key}-${col.key}`;
              cellRefs.current[cellId] = el;
            }}
          />
          <button
            className="cell-style-btn"
            onClick={(e) => {
              e.stopPropagation();
              openStylePopover(e, col.key);
            }}
          >
            <FiSettings size={12} />
          </button>
        </td>
      );
    }

    return (
      <td
        key={col.key}
        style={style}
        rowSpan={merge.rowSpan}
        colSpan={merge.colSpan}
        data-row-key={isPhaseNote ? row.stage_label : row.row_key}
        className={`${
          col.key === "room" &&
          (row.row_key?.startsWith("room_") ||
            [
              "clone",
              "momma",
              "veg_1",
              "temp_day",
              "temp_night",
              "fans",
              "fans_labels",
            ].includes(row.row_key))
            ? "room-id-cell"
            : ""
        } col-${col.key}`}
        dangerouslySetInnerHTML={{ __html: val }}
      />
    );
  };

  const renderStylePopover = () => {
    const style = activeStyleCell
      ? editData?.column_styles?.[activeStyleCell.colKey] || {}
      : {};

    return (
      <StylePopover
        styles={style}
        onChange={(newStyle) =>
          handleStyleChange(activeStyleCell.colKey, newStyle)
        }
        onClose={() => setActiveStyleCell(null)}
        position={{ x: activeStyleCell.x, y: activeStyleCell.y }}
      />
    );
  };

  return (
    <div className="grow-room-container">
      {loading && <FullScreenLoader />}

      {/* <div className="grow-room-header">
        <h1>Grow Room Master Sheet</h1>
      </div> */}

      <div className="grow-room-print-header">
        <div className="print-header-segment segment-1">
          <img
            src={frostedFarmLogo}
            alt="Frosted"
            className="print-header-logo"
          />
        </div>
        <div className="print-header-segment segment-2">
          <div className="print-header-date">
            Date: {dayjs().day(1).format("M/D/YYYY")}
          </div>
        </div>
        <div className="print-header-segment segment-3">
          <img
            src={frostedFarmLogo}
            alt="Frosted"
            className="print-header-logo"
          />
        </div>
        <div className="print-header-segment segment-4">
          <div className="print-header-homerun">5/9 HOME RUN DONE</div>
        </div>
        <div className="print-header-segment segment-5">
          <img src={route420} alt="420" className="print-header-logo" />
        </div>
        <div className="print-header-segment segment-6">
          <img
            src={frostedFarmLogo}
            alt="Frosted"
            className="print-header-logo"
          />
        </div>
      </div>

      <div className="grow-room-table-wrapper" ref={mainTableWrapperRef}>
        <table className="grow-room-table">
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <th key={col.key} className={`col-${col.key}`}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows
              .filter((r) => r.row_key.startsWith("room_"))
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((row) => (
                <tr key={row.row_key} data-row-key={row.row_key}>
                  {COLUMNS.map((col) => renderCell(row, col))}
                </tr>
              ))}
            {rows
              .filter((r) =>
                [
                  "clone",
                  "momma",
                  "veg_1",
                  "temp_day",
                  "temp_night",
                  "fans",
                  "fans_labels",
                ].includes(r.row_key),
              )
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((row) => (
                <tr
                  key={row.row_key}
                  className="grow-room-footer-row"
                  data-row-key={row.row_key}
                >
                  {COLUMNS.map((col) => renderCell(row, col))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="grow-room-phase-notes" ref={phaseNotesWrapperRef}>
        <table className="phase-notes-table">
          <thead>
            <tr>
              <th className="print-hide" style={{ width: "80px" }}>
                Action
              </th>
              <th style={{ width: "60px" }}>Room</th>
              <th style={{ width: "80px" }}>Stage</th>
              <th>Notes</th>
              <th className="print-hide" style={{ width: "80px" }}>
                Action
              </th>
              <th style={{ width: "60px" }}>Room</th>
              <th style={{ width: "80px" }}>Stage</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {[
              { left: "phase_v1", right: "phase_f1" },
              { left: "phase_v2", right: "phase_f2" },
              { left: "phase_v3", right: "phase_f3" },
              { left: null, right: "phase_21_day" },
              { left: null, right: "phase_40_day" },
            ].map((pair, idx) => {
              const leftRow = phaseNotes.find(
                (p) => p.stage_label === pair.left,
              ) || { stage_label: pair.left, note_value: "" };
              const rightRow = phaseNotes.find(
                (p) => p.stage_label === pair.right,
              ) || { stage_label: pair.right, note_value: "" };

              return (
                <tr key={idx}>
                  {leftRow.stage_label ? (
                    <>
                      {renderCell(leftRow, { key: "action" }, true)}
                      {renderCell(leftRow, { key: "room" }, true)}
                      {renderCell(leftRow, { key: "stage" }, true)}
                      {renderCell(leftRow, { key: "notes" }, true)}
                    </>
                  ) : (
                    <>
                      <td className="action-column-cell print-hide empty-phase-slot" />
                      <td className="col-room empty-phase-slot" />
                      <td className="col-stage empty-phase-slot" />
                      <td className="col-notes empty-phase-slot" />
                    </>
                  )}
                  {rightRow.stage_label ? (
                    <>
                      {renderCell(rightRow, { key: "action" }, true)}
                      {renderCell(rightRow, { key: "room" }, true)}
                      {renderCell(rightRow, { key: "stage" }, true)}
                      {renderCell(rightRow, { key: "notes" }, true)}
                    </>
                  ) : (
                    <>
                      <td className="action-column-cell print-hide empty-phase-slot" />
                      <td className="col-room empty-phase-slot" />
                      <td className="col-stage empty-phase-slot" />
                      <td className="col-notes empty-phase-slot" />
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editingRowKey && activeToolbar && (
        <RichTextToolbar onCommand={handleCommand} position={activeToolbar} />
      )}
      {editingRowKey && activeStyleCell && renderStylePopover()}

      <Popover
        open={Boolean(actionAnchorEl)}
        anchorEl={actionAnchorEl}
        onClose={handleActionClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          style: {
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            borderRadius: "4px",
            border: "1px solid #dee2e6",
          },
        }}
      >
        <MenuItem
          onClick={handleEditFromAction}
          style={{
            fontSize: "13px",
            gap: "8px",
            padding: "8px 16px",
          }}
        >
          <FiEdit3 size={14} />
          Edit
        </MenuItem>
      </Popover>
    </div>
  );
};

export default GrowRoom;
